import { useEffect, useRef, useState } from 'react';
import { useAuth } from './useAuth';
import { saveVisitWorker } from '../services/visitAssignment';

export function useVisitAllocation(onAssigned) {
  const { activeUserEmail, activeUserRoles, activeEmployeeId, loading: authLoading } = useAuth();
  const [workers, setWorkers] = useState([]);
  const [workersError, setWorkersError] = useState(null);
  const [savingAssignments, setSavingAssignments] = useState({});
  const [assignmentErrors, setAssignmentErrors] = useState({});
  const assignmentRequests = useRef(new Map());
  const roleHeader = (activeUserRoles || []).join(',');
  const canAssign = (activeUserRoles || []).some((role) => ['admin', 'kcs-super-user'].includes(role.toLowerCase()));

  useEffect(() => {
    const requests = assignmentRequests.current;
    setSavingAssignments({});
    setAssignmentErrors({});
    return () => {
      requests.forEach((controller) => controller.abort());
      requests.clear();
    };
  }, [activeEmployeeId, activeUserEmail, roleHeader]);

  useEffect(() => {
    setWorkers([]);
    setWorkersError(null);
    if (authLoading || !canAssign) return;
    const controller = new AbortController();
    fetch(`/api/workers?employeeId=${encodeURIComponent(activeEmployeeId || '167')}`, {
      signal: controller.signal,
      headers: {
        'x-kcs-user-email': activeUserEmail || '',
        'x-kcs-user-roles': roleHeader,
        'x-kcs-provider-employee-id': activeEmployeeId || '167',
      },
    }).then(async (response) => {
      if (!response.ok) throw new Error('Unable to load users for assignment.');
      const data = await response.json();
      if (!Array.isArray(data)) throw new Error('Unable to load users for assignment.');
      if (!controller.signal.aborted) {
        setWorkers(data.filter((worker) => worker.status === 'Active' && ['Worker', 'Admin'].includes(worker.type)));
      }
    }).catch((error) => {
      if (!controller.signal.aborted) setWorkersError(error.message);
    });
    return () => controller.abort();
  }, [authLoading, canAssign, activeEmployeeId, activeUserEmail, roleHeader]);

  const onAssign = async (visitId, workerId) => {
    if (!canAssign || authLoading || assignmentRequests.current.has(visitId)) return;
    const controller = new AbortController();
    assignmentRequests.current.set(visitId, controller);
    setSavingAssignments((current) => ({ ...current, [visitId]: true }));
    setAssignmentErrors((current) => ({ ...current, [visitId]: null }));
    try {
      const assignment = await saveVisitWorker(visitId, workerId, {
        employeeId: activeEmployeeId, userEmail: activeUserEmail, roleHeader, signal: controller.signal,
      });
      if (!controller.signal.aborted) onAssigned(visitId, assignment);
    } catch (error) {
      if (!controller.signal.aborted) setAssignmentErrors((current) => ({ ...current, [visitId]: error.message }));
    } finally {
      if (!controller.signal.aborted) {
        assignmentRequests.current.delete(visitId);
        setSavingAssignments((current) => ({ ...current, [visitId]: false }));
      }
    }
  };

  return { workers, workersError, canAssign, onAssign, savingAssignments, assignmentErrors };
}